import React, { useEffect, useCallback } from 'react';
import {
  arrayOf,
  bool,
  func,
  node,
  object,
  objectOf,
  oneOf,
  oneOfType,
  shape,
  string,
} from 'prop-types';

import { noop } from '@ajaibid/utils';
import { Input } from '@ajaibid/components';
import useField from './hooks/useField';

const FormRadio = (props) => {
  const {
    id,
    name,
    defaultValue,
    error: propsError,
    onChange,
    onError,
    onValid,
    options,
    validators,
    ...baseProps
  } = props;

  const [fieldValue = '', { updateField, updateForm, error: errorMessage, errorKey, showError }] =
    useField(name, { defaultValue, validators });

  const onChangeCb = useCallback((params) => onChange(params), [onChange]);
  const onErrorCb = useCallback((params) => onError(params), [onError]);
  const onValidCb = useCallback((params) => onValid(params), [onValid]);

  useEffect(() => {
    if (errorMessage) {
      onErrorCb({ errorMessage, errorKey });
    } else {
      onValidCb();
    }
  }, [errorMessage, errorKey, onErrorCb, onValidCb]);

  // TODO: this line breaks validator. Wait for QA regression, if it works fine without it, remove
  // useEffect(() => {
  //   if (propsError) {
  //     updateForm(name, fieldValue, propsError);
  //   }
  // }, [propsError, name, updateForm]);

  const handleChange = (event) => {
    onChangeCb(event.target.value);
    updateField(event.target.value);
  };

  return (
    <Input.Radio
      id={id}
      name={name}
      value={fieldValue}
      options={options}
      onChange={handleChange}
      // Only pass the error message when Form showError is true.
      error={showError ? propsError || errorMessage || '' : ''}
      {...baseProps}
    />
  );
};

const optionType = oneOfType([string, shape({ value: string, label: oneOfType([string, node]) })]);

FormRadio.propTypes = {
  // <Input.Radio> props
  id: string.isRequired,
  name: string.isRequired,
  className: string,
  disabled: bool,
  flexDirection: oneOf(['row', 'column']),
  options: arrayOf(optionType),
  style: object,
  // <Form.Radio> props
  defaultValue: optionType,
  error: string,
  onChange: func,
  onError: func,
  onValid: func,
  validators: objectOf(
    shape({
      validator: func,
      message: string,
    })
  ),
};

FormRadio.defaultProps = {
  // <Input.Radio> props
  className: '',
  disabled: false,
  flexDirection: 'column',
  options: [],
  style: {},
  // <Form.Radio> props
  defaultValue: '',
  error: '',
  onChange: noop,
  onError: noop,
  onValid: noop,
  validators: {},
};

export default FormRadio;
